import { useCallback } from 'react'
import { SprintDetail } from '../../../../lib/functions/sprint'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  SprintProductItemDetail,
  createSprintProductItemKey,
  createSprintProductItemAssigneeKey,
  getMaxDisplayOrderInSprint,
  isUnsetSprintRowUuid,
} from '../productBacklog'
import { SpiAdjustmentItem } from '../components/SprintAssignmentDialog'
import {
  createUnsetSprintPbiRow,
  insertRowsAfterSprint,
  sortPbiRowsWithinSprints,
} from './sprintOperations'

interface UseSprintAdjustmentProps {
  sprints: SprintDetail[]
  data: ProductBacklogRow[]
  setData: (data: ProductBacklogRow[]) => void
  incrementDataVersion: () => void
}

/**
 * 変更を分析し、追加・更新・削除の情報を返す
 */
const analyzeChanges = (
  items: SpiAdjustmentItem[],
  currentSpiMap: Map<string, SprintProductItemDetail>
) => {
  const currentSpiUuids = new Set([...currentSpiMap.keys()])
  const newSpiUuids = new Set(items.map(item => item.uuid))

  const itemsToAdd = items.filter(item => item.isNew)
  const itemsToUpdate = items.filter(
    item => !item.isNew && currentSpiMap.has(item.uuid)
  )
  const uuidsToRemove = [...currentSpiUuids].filter(
    uuid => !newSpiUuids.has(uuid)
  )

  const hasUpdates = itemsToUpdate.some(item => {
    const original = currentSpiMap.get(item.uuid)
    if (!original) return false
    return (
      item.estimatedHour !== (original.estimatedHour || 0) ||
      item.assignee?.uuid !== original.assignee?.uuid
    )
  })

  return {
    itemsToAdd,
    uuidsToRemove,
    hasAdditions: itemsToAdd.length > 0,
    hasRemovals: uuidsToRemove.length > 0,
    hasUpdates,
  }
}

/**
 * 新しいSPI配列を構築する
 */
const buildNewSprintProductItems = (
  items: SpiAdjustmentItem[],
  currentSpiMap: Map<string, SprintProductItemDetail>,
  data: ProductBacklogRow[]
): SprintProductItemDetail[] => {
  // スプリントごとの最大displayOrderをキャッシュ（data全体から計算）
  const maxDisplayOrderBySprint = new Map<string, number>()

  return items.map(item => {
    if (item.isNew) {
      // キャッシュになければdata全体から計算
      if (!maxDisplayOrderBySprint.has(item.sprintUuid)) {
        maxDisplayOrderBySprint.set(
          item.sprintUuid,
          getMaxDisplayOrderInSprint(data, item.sprintUuid)
        )
      }
      const maxOrder = maxDisplayOrderBySprint.get(item.sprintUuid)!
      const newDisplayOrder = maxOrder + 1
      maxDisplayOrderBySprint.set(item.sprintUuid, newDisplayOrder)

      return {
        uuid: item.uuid,
        sprintUuid: item.sprintUuid,
        estimatedHour: item.estimatedHour,
        taskEstimatedHour: 0,
        displayOrder: newDisplayOrder,
        lockVersion: 0,
        assignee: item.assignee,
        sprintGoal: null,
      }
    }
    const original = currentSpiMap.get(item.uuid)!
    return {
      ...original,
      estimatedHour: item.estimatedHour,
      assignee: item.assignee,
    }
  })
}

/**
 * 削除する行のUUIDを計算する
 */
const calculateRowUuidsToRemove = (
  uuidsToRemove: string[],
  currentSpiMap: Map<string, SprintProductItemDetail>,
  newSprintProductItems: SprintProductItemDetail[],
  targetPbiRows: ProductBacklogItemRow[]
): Set<string> => {
  // 削除されるSPIのUUID（個別削除）
  const rowUuidsToRemove = new Set<string>(uuidsToRemove)

  // スプリント全体が削除される場合の追加処理
  const removedSprintUuids = new Set<string>()
  uuidsToRemove.forEach(uuid => {
    const spi = currentSpiMap.get(uuid)
    if (spi) {
      removedSprintUuids.add(spi.sprintUuid)
    }
  })

  // 残るSPIがあるスプリントは除外
  newSprintProductItems.forEach(spi => {
    removedSprintUuids.delete(spi.sprintUuid)
  })

  // スプリント全体が削除される場合、そのスプリントの行も削除
  targetPbiRows.forEach(row => {
    if (removedSprintUuids.has(row.treeValue[0])) {
      rowUuidsToRemove.add(row.uuid)
    }
  })

  return rowUuidsToRemove
}

/**
 * 新規行を追加するアイテムをフィルタする
 */
const filterItemsToAddRows = (
  itemsToAdd: SpiAdjustmentItem[],
  targetPbiRows: ProductBacklogItemRow[]
): SpiAdjustmentItem[] => {
  const currentRowUuids = new Set(targetPbiRows.map(row => row.uuid))
  return itemsToAdd.filter(item => !currentRowUuids.has(item.uuid))
}

/**
 * SPIから行に同期するプロパティを返す
 */
const getSpiSyncProps = (
  spi: SprintProductItemDetail | undefined
): Partial<ProductBacklogItemRow> => {
  if (!spi) return {}
  return {
    assignee: spi.assignee,
    sprintGoal: spi.sprintGoal,
    estimatedHourPerSprint: spi.estimatedHour,
  }
}

/**
 * SPIに対応するPBI行を作成する
 */
const createPbiRowFromSpi = (
  baseRow: ProductBacklogItemRow,
  item: SpiAdjustmentItem,
  correspondingSpi: SprintProductItemDetail | undefined,
  newSprintProductItems: SprintProductItemDetail[]
): ProductBacklogItemRow => ({
  ...baseRow,
  uuid: item.uuid,
  treeValue: [item.sprintUuid, item.uuid],
  ...getSpiSyncProps(correspondingSpi),
  sprintProductItems: newSprintProductItems,
  added: true,
  edited: true,
  editedData: { ...baseRow.editedData },
})

/**
 * 編集状態データを構築する
 */
const buildEditedData = (
  items: SpiAdjustmentItem[],
  currentSpiMap: Map<string, SprintProductItemDetail>,
  existingEditedData: Record<string, unknown>
): Record<string, unknown> => {
  const editedData = { ...existingEditedData }

  for (const item of items) {
    const original = currentSpiMap.get(item.uuid)
    const estimatedHourKey = createSprintProductItemKey(
      item.sprintUuid,
      item.uuid
    )
    const assigneeKey = createSprintProductItemAssigneeKey(
      item.sprintUuid,
      item.uuid
    )

    if (item.isNew) {
      editedData[estimatedHourKey] = item.estimatedHour
      editedData[assigneeKey] = { assignee: item.assignee }
    } else if (original) {
      if (item.estimatedHour !== (original.estimatedHour || 0)) {
        editedData[estimatedHourKey] = item.estimatedHour
      }
      if (item.assignee?.uuid !== original.assignee?.uuid) {
        editedData[assigneeKey] = { assignee: item.assignee }
      }
    }
  }

  return editedData
}

/**
 * 未設定スプリントからの移動を処理する
 */
const handleUnsetSprintMove = (
  wasUnsetSprint: boolean,
  itemsToAdd: SpiAdjustmentItem[],
  itemsToAddRows: SpiAdjustmentItem[],
  targetPbiRows: ProductBacklogItemRow[],
  representativeRow: ProductBacklogItemRow,
  newSprintProductItems: SprintProductItemDetail[],
  newData: ProductBacklogRow[]
): {
  newData: ProductBacklogRow[]
  firstItemToMove: SpiAdjustmentItem | undefined
} => {
  if (!wasUnsetSprint) {
    return { newData, firstItemToMove: undefined }
  }

  const currentSprintUuidsWithRows = new Set(
    targetPbiRows.map(row => row.treeValue[0])
  )
  const sprintUuidsNeedingNewRows = new Set(
    itemsToAdd
      .filter(item => !currentSprintUuidsWithRows.has(item.sprintUuid))
      .map(item => item.sprintUuid)
  )

  if (sprintUuidsNeedingNewRows.size === 0) {
    return { newData, firstItemToMove: undefined }
  }

  const firstSprintUuid = [...sprintUuidsNeedingNewRows][0]
  const firstItemToMove = itemsToAddRows.find(
    item => item.sprintUuid === firstSprintUuid
  )

  if (!firstItemToMove) {
    return { newData, firstItemToMove: undefined }
  }

  const correspondingSpi = newSprintProductItems.find(
    spi => spi.uuid === firstItemToMove.uuid
  )
  const oldRowUuid = representativeRow.uuid

  // UNSET SPIが新しいアイテムに残っている場合は、古い行を維持する
  const keepUnsetRow = newSprintProductItems.some(spi =>
    isUnsetSprintRowUuid(spi.sprintUuid)
  )
  let updatedData = keepUnsetRow
    ? newData
    : newData.filter(r => r.uuid !== oldRowUuid)

  // 新しい行を作成して挿入
  const movedRow = createPbiRowFromSpi(
    representativeRow,
    firstItemToMove,
    correspondingSpi,
    newSprintProductItems
  )
  updatedData = insertRowsAfterSprint(updatedData, firstSprintUuid, [movedRow])

  return { newData: updatedData, firstItemToMove }
}

export const useSprintAdjustment = ({
  sprints,
  data,
  setData,
  incrementDataVersion,
}: UseSprintAdjustmentProps) => {
  const applySprintAdjustment = useCallback(
    (
      deliverable: ProductBacklogItemRow,
      items: SpiAdjustmentItem[]
    ): ProductBacklogRow[] | undefined => {
      const { originalUuid } = deliverable
      const sprintMapByUuid = Object.fromEntries(sprints.map(s => [s.uuid, s]))

      // 現在のSPIをマップ化
      const currentSpiMap = new Map(
        (deliverable.sprintProductItems || []).map(spi => [spi.uuid, spi])
      )

      // 変更を分析
      const {
        itemsToAdd,
        uuidsToRemove,
        hasAdditions,
        hasRemovals,
        hasUpdates,
      } = analyzeChanges(items, currentSpiMap)

      if (!hasAdditions && !hasRemovals && !hasUpdates) {
        return undefined
      }

      // 対象のPBI行を取得
      const targetPbiRows = data.filter(
        (row): row is ProductBacklogItemRow =>
          isProductBacklogItemRow(row) && row.originalUuid === originalUuid
      )

      if (targetPbiRows.length === 0) {
        return undefined
      }

      const representativeRow = targetPbiRows[0]
      const originalSprint = deliverable.wbsItem.sprint
      const originalSprints = deliverable.wbsItem.sprints
        ? [...deliverable.wbsItem.sprints]
        : []

      // 新しいSPI配列を構築
      const newSprintProductItems = buildNewSprintProductItems(
        items,
        currentSpiMap,
        data
      )

      // 削除する行のUUIDを計算
      const rowUuidsToRemove = calculateRowUuidsToRemove(
        uuidsToRemove,
        currentSpiMap,
        newSprintProductItems,
        targetPbiRows
      )

      // 新規行を作成するアイテム（既に行が存在するものを除外）
      const itemsToAddRows = filterItemsToAddRows(itemsToAdd, targetPbiRows)

      // 新しいデータ配列を構築
      let newData = data.filter(v => !rowUuidsToRemove.has(v.uuid))

      // representativeRowのSPIとwbsItem.sprint/sprintsを更新
      representativeRow.sprintProductItems = newSprintProductItems
      const newSprints = [
        ...new Set(newSprintProductItems.map(spi => spi.sprintUuid)),
      ]
        .map(uuid => sprintMapByUuid[uuid])
        .filter(Boolean)
      representativeRow.wbsItem.sprint =
        newSprints.length > 0 ? newSprints[0] : undefined
      representativeRow.wbsItem.sprints = newSprints

      // 編集状態を設定
      if (!representativeRow.editedData) {
        representativeRow.editedData = {}
      }
      if (!representativeRow.editedData.hasOwnProperty('wbsItem.sprint')) {
        representativeRow.editedData['wbsItem.sprint'] = originalSprint
        representativeRow.editedData['wbsItem.sprints'] = originalSprints
      }

      // 各SPIの編集キーを設定
      representativeRow.editedData = buildEditedData(
        items,
        currentSpiMap,
        representativeRow.editedData
      )
      representativeRow.edited = true

      // 未設定スプリントからの移動処理
      // SPIが0件、または全SPIがスプリント未設定の場合
      const wasUnsetSprint =
        currentSpiMap.size === 0 ||
        [...currentSpiMap.values()].every(spi =>
          isUnsetSprintRowUuid(spi.sprintUuid)
        )
      const unsetSprintMoveResult = handleUnsetSprintMove(
        wasUnsetSprint,
        itemsToAdd,
        itemsToAddRows,
        targetPbiRows,
        representativeRow,
        newSprintProductItems,
        newData
      )
      newData = unsetSprintMoveResult.newData
      const firstItemToMove = unsetSprintMoveResult.firstItemToMove

      // 追加行を作成
      for (const item of itemsToAddRows) {
        if (firstItemToMove && item.uuid === firstItemToMove.uuid) {
          continue
        }

        const correspondingSpi = newSprintProductItems.find(
          spi => spi.uuid === item.uuid
        )
        if (!correspondingSpi) continue

        const copiedRow = createPbiRowFromSpi(
          representativeRow,
          item,
          correspondingSpi,
          newSprintProductItems
        )
        copiedRow.assignedSprintName =
          sprintMapByUuid[item.sprintUuid]?.name ?? ''
        newData = insertRowsAfterSprint(newData, item.sprintUuid, [copiedRow])
      }

      // 全行削除後は未設定行を追加
      const hasRemainingRows = newData.some(
        row => isProductBacklogItemRow(row) && row.originalUuid === originalUuid
      )
      if (!hasRemainingRows) {
        newData.push(createUnsetSprintPbiRow(representativeRow))
      }

      // 全対象行に編集状態を伝播
      newData = newData.map(row => {
        if (isProductBacklogItemRow(row) && row.originalUuid === originalUuid) {
          const correspondingSpi = newSprintProductItems.find(
            spi => spi.uuid === row.uuid
          )
          return {
            ...row,
            sprintProductItems: newSprintProductItems,
            wbsItem: {
              ...row.wbsItem,
              sprint: newSprints.length > 0 ? newSprints[0] : undefined,
              sprints: newSprints,
            },
            ...getSpiSyncProps(correspondingSpi),
            assignedSprintName: correspondingSpi
              ? (sprintMapByUuid[correspondingSpi.sprintUuid]?.name ?? '')
              : '',
            edited: true,
            editedData: {
              ...row.editedData,
              ...representativeRow.editedData,
            },
          }
        }
        return row
      })

      // スプリント内の行をdisplayOrderでソート
      newData = sortPbiRowsWithinSprints(newData)

      setData(newData)
      incrementDataVersion()

      return newData
    },
    [sprints, data, setData, incrementDataVersion]
  )

  return { applySprintAdjustment }
}
